// The product's toasts, on Soft (style lab, final round 6, 2026-10-09): ONE
// line 40 high, as wide as its words; a raised capsule with the 1px highlight;
// a plain status mark with no plate under it (Phosphor: a check in a circle,
// an exclamation in a circle, a spinning notch while pending); the tail is the
// time, then the link mark. The stack is centred at the top, newest last, six
// at most (the seventh pushes the oldest out — never a trade still under way,
// and a trade's toast only after every other kind: fitStack) — unless
// Settings says otherwise:
// Toast position and Max toasts shown are read from settings/settingsStore,
// and with nothing saved they are exactly that. The module keeps its first
// name — the pixel toast of design lab #196 — because forty files import it.
//
// API повторяет ui/toast: push() → id, update(id) для pending → ok/fail
// (сценарий «купить → лоадинг → успех», когда придёт трейд-бэкенд).
// Контент — ReactNode: формулы тостов собирает вызывающая сторона
// (Buy {лого}{ТИКЕР} for 1.02 ◆ …), тост отвечает за корпус/стек/тайминги.

import { CheckCircleIcon, CircleNotchIcon, WarningCircleIcon, XIcon } from '@phosphor-icons/react'
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import { subscribeSettings, toastPosition, toastStackLimit } from '../settings/settingsStore'
import { figs } from './fig'
import styles from './pixel-toast.module.css'
import placement from './toast-placement.module.css'

export type PixelToastTone = 'pending' | 'ok' | 'fail' | 'info'

/** Карточки одного стека нотификаций: однострочный тост исполнения и
 *  двухэтажный алерт трекнутого кошелька (финалы лабы #196). `group` — one
 *  trade from several wallets (2026-11-05): its own body (GroupTradeToast),
 *  held on screen while the pointer is on it, so a long list can be read. */
export type PixelToastKind = 'toast' | 'alert' | 'group'

export interface PixelToastInput {
  tone: PixelToastTone
  /** Кастомная иконка статуса вместо глифа тона (2026-08-30: копи-тост —
   *  серая иконка копирования, не зелёная ✓). */
  icon?: ReactNode
  /** Показать ✕ справа (2026-08-30: копи-тост). У fail-тостов ✕ есть всегда. */
  close?: boolean
  /** alert рисует свой корпус (две строки, свои контролы) и не показывает
   *  иконку статуса; content может быть функцией от dismiss. */
  kind?: PixelToastKind
  content: ReactNode | ((api: { dismiss: () => void }) => ReactNode)
  /** хвост справа: время исполнения/ссылка на tx; опционален */
  tail?: ReactNode
  /** авто-скрытие, мс; по умолчанию 3000 (фидбек), ∞ для pending */
  duration?: number
  onClick?: () => void
  /** A toast of a trade of the user's own. Unset, a toast is a trade's when
   *  it starts pending — the one producer of those is the trade lifecycle
   *  (hooks/useTradeLifecycleToast), whose toast is pending until the trade
   *  ends. See fitStack for what it changes. */
  trade?: boolean
}

interface PixelToast extends PixelToastInput {
  id: number
  leaving?: boolean
}

/** What fitStack needs to know of a toast. */
export type StackEntry = {
  id: number
  tone: PixelToastTone
  trade?: boolean
  leaving?: boolean
}

/**
 * The stack held to "Max toasts shown" (the operator, 2026-10-18: trade
 * toasts are not pushed out — and the cap still holds trades, so the screen is
 * never flooded by them). While more than `limit` are shown, one goes, in this
 * order:
 *   1. the oldest toast that is not a trade's (a copy, an error, a prompt, a
 *      wallet alert);
 *   2. only when every one shown is a trade's: the oldest FINISHED trade;
 *   3. a trade still under way (pending) never goes.
 * `keep` is the toast just pushed or just finished: it is never the one that
 * goes. So the stack can run over the cap only by trades under way:
 * shown <= limit + pending trades. A toast already leaving is on its way out
 * and is not counted.
 */
export function fitStack<T extends StackEntry>(
  list: readonly T[],
  limit: number,
  keep: number
): T[] {
  const out = [...list]
  const shown = () => out.filter((t) => !t.leaving).length
  while (shown() > limit) {
    const can = (t: T) => !t.leaving && t.id !== keep
    const victim =
      out.find((t) => can(t) && !t.trade) ??
      out.find((t) => can(t) && t.trade && t.tone !== 'pending')
    if (!victim) break
    out.splice(out.indexOf(victim), 1)
  }
  return out
}

interface PixelToastApi {
  push: (t: PixelToastInput) => number
  update: (id: number, patch: Partial<PixelToastInput>) => void
  dismiss: (id: number) => void
  /** A trade of the user's own is under way (its toast is pending). */
  busy: () => boolean
}

const PixelToastContext = createContext<PixelToastApi | null>(null)

export function usePixelToast(): PixelToastApi {
  const api = useContext(PixelToastContext)
  if (!api) throw new Error('usePixelToast must be used inside <PixelToastProvider>')
  return api
}

/** Для презентационных компонентов, которые могут рендериться без провайдера
 *  (изолированные тесты таблицы): тост — опциональное улучшение, не требование. */
export function useOptionalPixelToast(): PixelToastApi | null {
  return useContext(PixelToastContext)
}

const EXIT_MS = 160

/** The pixel spinner of lab #196 — a wave round a ring. The toasts no longer
 *  wear it; Instant Trade's quick buttons do while they cool down. */
export function PxSpinner() {
  return (
    <span className={styles.pxSpin} aria-label="pending">
      <i />
      <i />
      <i />
      <i />
      <i />
      <i />
      <i />
      <i />
    </span>
  )
}

/** The Soft spinner: Phosphor's notch, turning. A toast that is pending wears
 *  it, and so does a login button while its provider answers. */
export function SoftSpinner({ size = 16 }: { size?: number }) {
  return (
    <span className={styles.spin} aria-label="pending">
      <CircleNotchIcon size={size} weight="bold" aria-hidden="true" />
    </span>
  )
}

// The lab's marks: a check in a circle, an exclamation in a circle. A prompt
// ("Log in to trade") was a red exclamation before and still is.
// The grouped trade toast's rows wear them at 14.
export function StatusSlot({ tone, size = 16 }: { tone: PixelToastTone; size?: number }) {
  if (tone === 'pending') return <SoftSpinner size={size} />
  if (tone === 'ok') {
    return <CheckCircleIcon size={size} weight="bold" className={styles.okInk} aria-hidden="true" />
  }
  return (
    <WarningCircleIcon size={size} weight="bold" className={styles.failInk} aria-hidden="true" />
  )
}

export function PixelToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<PixelToast[]>([])
  const shownRef = useRef<PixelToast[]>([])
  shownRef.current = toasts
  const nextId = useRef(1)
  const timers = useRef(new Map<number, number>())
  /** the leaving toasts' exit timers */
  const exits = useRef(new Set<number>())
  // gone with the provider: no timer outlives it (a toast's dismiss or exit
  // firing after the page — or a test's environment — is torn down)
  useEffect(() => {
    const armed = timers.current
    const leaving = exits.current
    return () => {
      for (const t of armed.values()) window.clearTimeout(t)
      for (const t of leaving) window.clearTimeout(t)
      armed.clear()
      leaving.clear()
    }
  }, [])
  const stackRef = useRef<HTMLDivElement>(null)

  // Top-layer (2026-08-30, фидбек: тост из поиска проваливался под нативный
  // <dialog> — z-index против top-layer не играет). Стек — manual-popover;
  // на каждом пуше переоткрываем, чтобы встать ПОВЕРХ открытых диалогов.
  useEffect(() => {
    const el = stackRef.current
    if (!el) return
    if (typeof el.showPopover !== 'function') {
      // jsdom / старые браузеры — обычный fixed + z-index
      el.removeAttribute('popover')
      return
    }
    try {
      if (toasts.length > 0) {
        if (el.matches(':popover-open')) el.hidePopover()
        el.showPopover()
      } else if (el.matches(':popover-open')) {
        el.hidePopover()
      }
    } catch {
      el.removeAttribute('popover')
    }
  }, [toasts])

  const clearTimer = useCallback((id: number) => {
    const t = timers.current.get(id)
    if (t != null) {
      window.clearTimeout(t)
      timers.current.delete(id)
    }
  }, [])

  const dismiss = useCallback(
    (id: number) => {
      clearTimer(id)
      // двухфазный уход: помечаем leaving → убираем после анимации
      setToasts((list) => list.map((t) => (t.id === id ? { ...t, leaving: true } : t)))
      const exit = window.setTimeout(() => {
        exits.current.delete(exit)
        setToasts((list) => list.filter((t) => t.id !== id))
      }, EXIT_MS)
      exits.current.add(exit)
    },
    [clearTimer]
  )

  const arm = useCallback(
    (id: number, t: PixelToastInput) => {
      clearTimer(id)
      const ms = t.duration ?? (t.tone === 'pending' ? 0 : 3000)
      if (ms > 0)
        timers.current.set(
          id,
          window.setTimeout(() => dismiss(id), ms)
        )
    },
    [dismiss, clearTimer]
  )

  const push = useCallback(
    (t: PixelToastInput) => {
      const id = nextId.current++
      // the stack holds as many as Settings says (six unless it was set); the
      // next one pushes out the oldest that is not a trade under way (fitStack)
      const limit = toastStackLimit()
      const toast: PixelToast = { ...t, id, trade: t.trade ?? t.tone === 'pending' }
      setToasts((list) => fitStack([...list, toast], limit, id))
      arm(id, t)
      return id
    },
    [arm]
  )

  const update = useCallback(
    (id: number, patch: Partial<PixelToastInput>) => {
      setToasts((list) => {
        const cur = list.find((t) => t.id === id)
        if (cur) arm(id, { ...cur, ...patch })
        const next = list.map((t) => (t.id === id ? { ...t, ...patch } : t))
        // a trade that has just ended no longer holds its place over the cap:
        // the stack is fitted again, the trade's own result staying
        const ended =
          cur?.tone === 'pending' && patch.tone !== undefined && patch.tone !== 'pending'
        return ended ? fitStack(next, toastStackLimit(), id) : next
      })
    },
    [arm]
  )

  // Where the stack stands: top centre unless Settings moved it, and it moves
  // the moment the setting does. The snapshot is the place itself, so another
  // setting's write (a slider dragged in Settings) does not redraw the
  // provider — and with it every surface that holds its context.
  const [v, h] = useSyncExternalStore(subscribeSettings, toastPosition).toLowerCase().split(' ')
  const busy = useCallback(
    () => shownRef.current.some((t) => t.trade && t.tone === 'pending' && !t.leaving),
    []
  )

  return (
    <PixelToastContext.Provider value={{ push, update, dismiss, busy }}>
      {children}
      <div
        ref={stackRef}
        className={`${styles.stack} ${placement.at}`}
        data-v={v}
        data-h={h}
        aria-live="polite"
        popover="manual"
      >
        {toasts.map((t) =>
          // алерт кошелька — свой корпус (внутри свои кнопки: ✕ и колокол),
          // поэтому НЕ button-обёртка; тост исполнения — как был
          t.kind === 'group' ? (
            // the grouped trade: its body draws its own head, rows and ✕. The
            // timer waits while the pointer is on it and starts over when it
            // leaves (a trade still under way has none). Updated in place: the
            // same key, so its rows and its scroll stay where they are.
            <div
              key={t.id}
              className={styles.groupCard}
              data-leaving={t.leaving || undefined}
              onMouseEnter={() => clearTimer(t.id)}
              onMouseLeave={() => arm(t.id, t)}
            >
              {typeof t.content === 'function'
                ? t.content({ dismiss: () => dismiss(t.id) })
                : t.content}
            </div>
          ) : t.kind === 'alert' ? (
            <div key={t.id} className={styles.alertCard} data-leaving={t.leaving || undefined}>
              {typeof t.content === 'function'
                ? t.content({ dismiss: () => dismiss(t.id) })
                : t.content}
            </div>
          ) : t.tone === 'fail' || t.close === true ? (
            // Ошибка — НЕ button и не дисмиссится кликом по телу (2026-08-29,
            // аудит D: случайный клик закрывал ошибку, которую хотели читать).
            // Только крестик. onClick тела всё ещё работает, если задан.
            // close:true (копи-тост) — тот же корпус с ✕, но тело дисмиссит.
            <div
              key={t.id}
              className={styles.toast}
              data-leaving={t.leaving || undefined}
              role={t.tone === 'fail' ? 'alert' : 'status'}
              onClick={() => {
                if (t.tone === 'fail') {
                  t.onClick?.()
                  return
                }
                try {
                  t.onClick?.()
                } finally {
                  dismiss(t.id)
                }
              }}
              onKeyDown={(e) => {
                // клавиатурный путь — кнопка ✕; тело реагирует на Enter/Esc
                if (e.key !== 'Enter' && e.key !== 'Escape') return
                if (t.tone === 'fail') t.onClick?.()
                else dismiss(t.id)
              }}
            >
              <span className={`${styles.tIcon} ${t.icon != null ? styles.tIconCustom : ''}`}>
                {t.icon != null ? t.icon : <StatusSlot tone={t.tone} />}
              </span>
              <span className={styles.tText}>
                {typeof t.content === 'function'
                  ? t.content({ dismiss: () => dismiss(t.id) })
                  : figs(t.content)}
              </span>
              {t.tail != null && <span className={styles.tTail}>{figs(t.tail)}</span>}
              <button
                type="button"
                className={styles.tClose}
                aria-label="Dismiss"
                onClick={(e) => {
                  e.stopPropagation()
                  dismiss(t.id)
                }}
              >
                <XIcon size={12} weight="bold" aria-hidden="true" />
              </button>
            </div>
          ) : (
            <button
              key={t.id}
              type="button"
              className={styles.toast}
              data-leaving={t.leaving || undefined}
              onClick={() => {
                try {
                  t.onClick?.()
                } finally {
                  dismiss(t.id)
                }
              }}
            >
              <span className={`${styles.tIcon} ${t.icon != null ? styles.tIconCustom : ''}`}>
                {t.icon != null ? t.icon : <StatusSlot tone={t.tone} />}
              </span>
              <span className={styles.tText}>
                {typeof t.content === 'function'
                  ? t.content({ dismiss: () => dismiss(t.id) })
                  : figs(t.content)}
              </span>
              {t.tail != null && <span className={styles.tTail}>{figs(t.tail)}</span>}
            </button>
          )
        )}
      </div>
    </PixelToastContext.Provider>
  )
}
